import { useAnalytics } from "@codat/analytics";
import { useCodatAuth } from "@codat/client-cookie-manager";
import {
  Button,
  Card,
  RadioButton,
  TextInput,
  Typography,
  UserIcon,
  useToast,
} from "@codat/orchard-ui";
import { useUserInfoMutation, useUserInfoQuery } from "api/userInfo";
import { AxiosError } from "axios";
import { useUserState } from "contexts/UserStateContext";
import React, { ChangeEvent, useEffect, useState } from "react";

import CardHeader from "components/CardHeader";

import { ApiError } from "../../../api/users";
import { useUserManagementService } from "../../UserManagement/UserManagementService";
import styles from "../AccountSettings.module.css";
import { getAuth0idpFromToken } from "api/setup";

export function UserDetails() {
  const { sendEvent } = useAnalytics();
  const { addToast } = useToast();
  const { userManager, authType, userData, signIn } = useCodatAuth();
  const { userState, refetch: refetchUserState } = useUserState();
  const [, updateUserInfo] = useUserInfoMutation();

  const [isSaving, setIsSaving] = useState(false);
  const [name, setName] = useState("");
  const [isTechnicalUser, setIsTechnicalUser] = useState(false);
  const [
    { data: userInfoQueryData, error: userInfoQueryError },
    refetchUserInfo,
  ] = useUserInfoQuery();

  const isNameInvalid = name.length < 3 || name.length > 256;
  const technicalUserDisabled =
    !userInfoQueryData && userInfoQueryError?.response?.status != 404;

  const userManagementService = useUserManagementService()?.editUser;

  if (!userManagementService) {
    throw new Error("No User Management Service provider");
  }

  useEffect(() => {
    if (userInfoQueryData) {
      setIsTechnicalUser(userInfoQueryData.isTechnicalUser);
    }

    if (userInfoQueryError?.response?.status === 404) {
      setIsTechnicalUser(false);
    }
  }, [userInfoQueryData, userInfoQueryError]);

  useEffect(() => {
    if (userState) setName(userState.userName);
  }, [userState]);

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    sendEvent("User name input - Change");
    setName(event.target.value);
  };

  const handleIsTechnicalUserChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    sendEvent("Is technical user radio - Change");
    setIsTechnicalUser(event.target.value === "true");
  };

  const token = userData?.access_token;

  const isNameChangeAllowed =
    (authType === "Auth0" && getAuth0idpFromToken(token) === "database") ||
    authType === "OIDC";

  const handleSave = () => {
    sendEvent("Save Changes Button - Click");

    if (
      userState.userName !== name ||
      userInfoQueryData?.isTechnicalUser !== isTechnicalUser
    ) {
      setIsSaving(true);
    }

    if (userState.userName !== name && isNameChangeAllowed) {
      userManagementService
        .editUser({
          id: userState.userId,
          name: name,
          email: userState.email,
          role: userState.userRole,
          enabled: true,
          confirmed: true,
          infoMessage: "",
        })
        .then(() => {
          if (authType === "OIDC") {
            // Get new token so userState is refetched with new data
            userManager.signinSilent().then(() => {
              addToast({
                message: "Name Updated",
                subText: `To ${name}`,
                variant: "success",
                icon: <UserIcon />,
              });
              refetchUserState();
              setIsSaving(false);
            });
          } else {
            signIn();
            addToast({
              message: "Name Updated",
              subText: `To ${name}`,
              variant: "success",
              icon: <UserIcon />,
            });
            refetchUserState();
            setIsSaving(false);
          }
        })
        .catch((error: AxiosError<ApiError>) => {
          const errorMessage = error.response?.data?.error ?? "Unknown error";
          addToast({
            message: `Unable to update user: ${errorMessage}`,
            variant: "error",
          });
          setIsSaving(false);
        });
    }

    if (userInfoQueryData?.isTechnicalUser !== isTechnicalUser) {
      updateUserInfo({ data: { isTechnicalUser } })
        .then(() => {
          addToast({
            message: "User type updated",
            subText: isTechnicalUser
              ? "You are now a technical user"
              : "You are no longer a technical user",
            variant: "success",
            icon: <UserIcon />,
          });
          refetchUserInfo();
          setIsSaving(false);
        })
        .catch((error: AxiosError<ApiError>) => {
          const errorMessage = error.response?.data?.error ?? "Unknown error";
          addToast({
            message: `Unable to update user: ${errorMessage}`,
            variant: "error",
          });
          setIsSaving(false);
        });
    }
  };

  return (
    <Card className={styles.card}>
      <CardHeader
        title="Profile"
        subtitle="Manage details of your personal profile."
      />
      <TextInput
        label="Full Name"
        id="fullname-input"
        data-testid="fullname-input"
        defaultValue={name}
        onChange={handleNameChange}
        error={userState && isNameInvalid}
        errorMessage={"Please enter a name between 3 and 256 characters"}
        disabled={!userState || !isNameChangeAllowed}
        minLength={3}
        maxLength={256}
      />
      {!isNameChangeAllowed && (
        <div className={styles.disabledBlock}>
          <Typography
            variant="heading_sm"
            as="h4"
            className={styles.disabledText}
          >
            You cannot edit your name
          </Typography>
          <Typography variant="body_sm" as="p" className={styles.disabledText}>
            Your name is set via your login provider.
          </Typography>
        </div>
      )}
      <Typography as="p">
        Are you involved in the technical implementation of your Codat solution?
      </Typography>
      <RadioButton
        name="technical-user-input"
        id="technical-user-input-true"
        data-testid="technical-user-input-true"
        label="Yes, I will be coding"
        value="true"
        isChecked={isTechnicalUser}
        onChange={handleIsTechnicalUserChange}
        disabled={technicalUserDisabled}
      />
      <RadioButton
        name="technical-user-input"
        id="technical-user-input-false"
        data-testid="technical-user-input-false"
        label="No, I will not be coding"
        value="false"
        isChecked={!isTechnicalUser}
        onChange={handleIsTechnicalUserChange}
        disabled={technicalUserDisabled}
      />
      <div className={styles.buttonContainer}>
        <Button
          data-testid="save-profile-button"
          label="Save Changes"
          onClick={handleSave}
          disabled={(!userInfoQueryData && !userState) || isNameInvalid}
          isLoading={isSaving}
        />
      </div>
    </Card>
  );
}
